// Split out of InstanceDetailPage.tsx, then migrated onto components/ui.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBindDomain, useUnbindDomain } from '../../hooks/useAddons'
import { useAuthStore } from '../../store/authStore'
import type { Instance } from '../../api/client'
import { Alert, Button, Card, CardHeader, Input, LinkButton } from '../../components/ui'
import { extractApiError } from './shared'

export function DomainsTab({ instance }: { instance: Instance }) {
  const [domain, setDomain] = useState('')
  const [error, setError] = useState('')
  const [unbindError, setUnbindError] = useState('')
  const bindMutation = useBindDomain(instance.id)
  const unbindMutation = useUnbindDomain(instance.id)
  const { t } = useTranslation()
  const rawPlan = useAuthStore((s) => s.plan)
  const role = useAuthStore((s) => s.role)
  const plan = rawPlan ?? 'free'
  const isPaidPlan = plan !== 'free' || role === 'admin'

  const handleBind = () => {
    setError('')
    bindMutation.mutate(domain, {
      onSuccess: () => setDomain(''),
      onError: (e) => setError(extractApiError(e) ?? t('detail.domainsExtra.bindError')),
    })
  }

  const handleUnbind = () => {
    setUnbindError('')
    unbindMutation.mutate(instance.custom_domain!, {
      onError: (e) => setUnbindError(extractApiError(e) ?? t('detail.domains.unbindError')),
    })
  }

  return (
    <div className="space-y-4">
      {!instance.custom_domain ? (
        <Card className="space-y-3">
          <CardHeader
            title={t('detail.domains.bindTitle')}
            description={isPaidPlan ? t('detail.domains.bindHint') : t('detail.domains.planRequiredHint')}
          />
          {error && <Alert tone="danger">{error}</Alert>}
          <div className="flex gap-2">
            <Input
              type="text"
              fullWidth={false}
              className="flex-1 disabled:bg-gray-100 disabled:text-gray-400"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder={t('detail.domains.placeholder')}
              aria-label={t('detail.domains.inputLabel')}
              disabled={!isPaidPlan}
            />
            <Button
              onClick={handleBind}
              disabled={!isPaidPlan || !domain}
              loading={bindMutation.isPending}
            >
              {t('detail.domains.bind')}
            </Button>
          </div>
          {!isPaidPlan && (
            <p className="text-xs text-amber-600">{t('detail.domains.planRequired')}</p>
          )}
        </Card>
      ) : (
        <Card>
          <CardHeader
            title={t('detail.domains.boundTitle')}
            description={
              <a href={`https://${instance.custom_domain}`} target="_blank" rel="noopener noreferrer"
                className="text-brand-600 hover:text-brand-700 block">
                {instance.custom_domain} ↗
              </a>
            }
            actions={
              <LinkButton tone="danger" onClick={handleUnbind} disabled={unbindMutation.isPending}>
                {unbindMutation.isPending ? t('detail.domains.unbinding') : t('detail.domains.unbind')}
              </LinkButton>
            }
          />
          {unbindError && <Alert tone="danger" className="mt-3">{unbindError}</Alert>}
        </Card>
      )}

      {/* The note is an interpolated HTML string (bold + <code> spans), so it
          goes in as markup rather than children. `yellow` folds onto the
          warning role, which is `amber`. */}
      <Alert tone="warning">
        <span dangerouslySetInnerHTML={{ __html: t('detail.domains.dnsNote', {
          domain: instance.custom_domain || 'yourdomain.com',
          cnameTarget: new URL(instance.subdomain_url.startsWith('http') ? instance.subdomain_url : `https://${instance.subdomain_url}`).host,
        }) }} />
      </Alert>
    </div>
  )
}
